import { action, computed, observable } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import {
  RequestedSupportingItem,
  type RequestedSupportingItemProps,
} from './RequestedSupportingItem'
import type { SupportingItemInput } from './supporting-item-input'
import { NotesSupportingItemInput } from '@/features/jobs/info-gathering/notes/NotesSupportingItemInput'
import { PersonalInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/PersonalInfoSupportingItemInput'
import { SpousalInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/SpousalInfoSupportingItemInput'
import { DependentsInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/DependentsInfoSupportingItemInput'
import { BusinessInfoSupportingItemInput } from '@/features/jobs/info-gathering/business-info/BusinessInfoSupportingItemInput'
import { PersonalRefundMethodInfoSupportingItemInput } from '@/features/jobs/info-gathering/tax-refund-method/PersonalRefundMethodInfoSupportingItemInput'
import { BusinessRefundMethodInfoSupportingItemInput } from '@/features/jobs/info-gathering/tax-refund-method/BusinessRefundMethodInfoSupportingItemInput'

/**
 * Props for {@link Checklist}.
 */
export type ChecklistProps = ModelProps<Checklist> & {
  sections: ChecklistSectionProps[]
}

/**
 * A checklist of items to complete for a job.
 */
export class Checklist extends Model<Checklist> {
  /**
   * ID of the job that the checklist is for.
   */
  jobId = Id.none

  readonly sections = collection({
    create: () => new ChecklistSection(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  /**
   * The notes section, if requested.
   */
  @computed get notesSection() {
    return this._getSection('NOTES')
  }

  /**
   * The personal information section, if requested.
   */
  @computed get personalInfoSection() {
    return this._getSection('PERSONAL_INFORMATION')
  }

  /**
   * The business information section, if requested.
   */
  @computed get businessInfoSection() {
    return this._getSection('BUSINESS_INFORMATION')
  }

  /**
   * Gets a requested supporting item by ID.
   */
  getItem(id: Id) {
    for (const section of this.sections.items) {
      const item = section.requestedSupportingItems.get(id)
      if (item) {
        return item
      }
    }

    return null
  }

  /** @inheritDoc */
  @action.bound
  override set({ sections, ...props }: Partial<ChecklistProps>): this {
    if (sections) {
      this.sections.reset(sections)
    }

    return super.set(props)
  }

  private _getSection(id: WellKnownSectionCategoryId) {
    return this.sections.items.find((section) => section.id === id) ?? null
  }
}

/**
 * Props for {@link ChecklistSection}.
 */
export type ChecklistSectionProps = ModelProps<ChecklistSection> & {
  requestedSupportingItems: RequestedSupportingItemProps[]
}

/**
 * A section within a checklist.
 */
export class ChecklistSection extends Model<ChecklistSection> {
  id = Id.none

  @observable accessor name = ''
  @observable accessor heading = ''
  @observable accessor description = ''

  readonly requestedSupportingItems = collection({
    create: () => new RequestedSupportingItem(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  /**
   * The notes item, if requested.
   */
  @computed get notesSupportingItem() {
    return this._getItemByInputType(NotesSupportingItemInput)
  }

  /**
   * The personal info item, if requested.
   */
  @computed get personalInfoSupportingItem() {
    return this._getItemByInputType(PersonalInfoSupportingItemInput)
  }

  /**
   * The dependents info item, if requested.
   */
  @computed get dependentInfoSupportingItem() {
    return this._getItemByInputType(DependentsInfoSupportingItemInput)
  }

  /**
   * The spousal info item, if requested.
   */
  @computed get spousalInfoSupportingItem() {
    return this._getItemByInputType(SpousalInfoSupportingItemInput)
  }

  /**
   * The business info item, if requested.
   */
  @computed get businessInfoSupportingItem() {
    return this._getItemByInputType(BusinessInfoSupportingItemInput)
  }

  /**
   * The business refund method item, if requested.
   */
  @computed get businessRefundMethodInfoSupportingItem() {
    return this._getItemByInputType(BusinessRefundMethodInfoSupportingItemInput)
  }

  /**
   * The personal refund method item, if requested.
   */
  @computed get personalRefundMethodInfoSupportingItem() {
    return this._getItemByInputType(PersonalRefundMethodInfoSupportingItemInput)
  }

  /**
   * Whether every required item has been completed.
   */
  @computed get requirementsCompleted(): boolean {
    return this.requestedSupportingItems.every(
      (item) => !item.required || item.completed,
    )
  }

  /**
   * Number of completed items.
   */
  @computed get completedCount(): number {
    return this.requestedSupportingItems.filter((item) => item.completed).length
  }

  /**
   * Number of requested items.
   */
  @computed get totalCount(): number {
    return this.requestedSupportingItems.length
  }

  /** @inheritDoc */
  @action.bound
  override set({
    requestedSupportingItems,
    ...props
  }: Partial<ChecklistSectionProps>): this {
    if (requestedSupportingItems) {
      this.requestedSupportingItems.reset(requestedSupportingItems)
    }

    return super.set(props)
  }

  private _getItemByInputType<T extends SupportingItemInput>(
    type: new () => T,
  ): RequestedSupportingItem<T> | null {
    return (
      this.requestedSupportingItems.items.find(
        (item): item is RequestedSupportingItem<T> =>
          item.input instanceof type,
      ) ?? null
    )
  }
}

/**
 * Well-known IDs of section categories.
 */
export type WellKnownSectionCategoryId =
  'PERSONAL_INFORMATION' | 'BUSINESS_INFORMATION' | 'NOTES'
